import type { GameInstanceConfig, GamePlugin, RoundContext } from '../types';
import { buildSkinsPlayers, computeSkins } from '../helpers';
import {
  applyMoneyToNetOwed,
  getLegStakes,
  getMoneyAmount,
  getScoringMode,
  getStakeConfig,
} from '../stakes-helpers';
import { computeAllNassauMatches, isNassauFormat } from '../nassau-matches';

export const skinsPlugin: GamePlugin = {
  id: 'skins',
  label: 'Skins',
  description: 'Win the hole outright, take the skin',
  requiresTeams: false,
  isMatch: false,
  minPlayers: 2,
  defaultStakes: { scoringType: 'money', money: 5, scoringMode: 'net', carryover: true },
  compute(ctx: RoundContext, config: GameInstanceConfig) {
    const stake = getStakeConfig(ctx.stakes, 'skins');
    const keys = config.playerKeys?.length ? config.playerKeys : ctx.players.map((p) => p.playerKey);
    const players = buildSkinsPlayers(ctx, 'skins', keys);
    const perHole = getMoneyAmount(stake, 'perHole');
    const mode = getScoringMode(stake);
    const carryover = stake.carryover !== false;
    const result = computeSkins(players, mode, perHole, carryover);
    const me = ctx.players.find((p) => p.you);
    const mine = me ? (result.totals[me.playerKey] || 0) : 0;
    const skinWinners = Object.entries(result.totals).filter(([, v]) => v > 0).map(([k]) => k);
    return {
      formatId: 'skins',
      label: 'Skins',
      summary: me ? `${me.name} has ${mine} skin${mine === 1 ? '' : 's'}` : 'Skins running',
      netOwed: applyMoneyToNetOwed(
        Object.fromEntries(Object.entries(result.net).map(([k, v]) => [k, Math.round(v * 100)])),
        stake,
      ),
      winners: skinWinners,
      detail: result as unknown as Record<string, unknown>,
    };
  },
};

function nassauCompute(
  ctx: RoundContext,
  config: GameInstanceConfig,
  formatId: string,
  label: string,
): ReturnType<GamePlugin['compute']> {
  const stake = getStakeConfig(ctx.stakes, formatId);
  const results = computeAllNassauMatches(ctx, formatId, config);
  if (!results.length) {
    return { formatId, label, summary: 'Add at least one matchup', netOwed: {} };
  }

  const netCents: Record<string, number> = {};
  const standings: Array<{ playerKey: string; name: string; value: string; rank: number }> = [];
  results.forEach((r, i) => {
    Object.entries(r.netCents).forEach(([k, v]) => {
      netCents[k] = (netCents[k] || 0) + v;
    });
    standings.push({
      playerKey: r.match.id,
      name: r.label,
      value: r.summary,
      rank: i + 1,
    });
  });

  const summary = results.length === 1
    ? `${results[0].label} · ${results[0].summary}`
    : `${results.length} Nassau matches`;

  const winners = results.flatMap((r) => (r.winner ? [r.winner] : []));

  return {
    formatId,
    label: results.length > 1 ? `${label} (${results.length})` : label,
    summary,
    standings,
    netOwed: applyMoneyToNetOwed(netCents, stake),
    winners,
    detail: { matches: results } as unknown as Record<string, unknown>,
  };
}

export const nassauPlugin: GamePlugin = {
  id: 'nassau',
  label: 'Nassau',
  description: 'Front 9, back 9, and overall',
  requiresTeams: false,
  isMatch: true,
  minPlayers: 2,
  defaultStakes: {
    scoringType: 'money',
    money: { front: 10, back: 10, total: 10 },
    scoringMode: 'net',
    pressesEnabled: false,
  },
  compute(ctx, config) {
    return nassauCompute(ctx, config, 'nassau', 'Nassau');
  },
};

export const teamNassauPlugin: GamePlugin = {
  ...nassauPlugin,
  id: 'team-nassau',
  label: 'Team Nassau',
  description: '2v2 — front, back, and overall',
  requiresTeams: true,
  minPlayers: 4,
  compute(ctx, config) {
    return nassauCompute(ctx, config, 'team-nassau', 'Team Nassau');
  },
};
